"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { fetchElements, applyUserTranslationsAllTypes } from "@/lib/utils";
import type {
  FederPAI,
  FederPAIProject,
  FederPAIOperaciones,
  FederPAIContratos,
  FederPAITransacciones,
  FederPAISpb,
  FederPAIIndicator,
} from "@/types/types";
import { useTranslations } from "next-intl";

interface FondoFederContextType {
  federPais: FederPAI[];
  projects: FederPAIProject[];
  operaciones: FederPAIOperaciones[];
  contratos: FederPAIContratos[];
  transacciones: FederPAITransacciones[];
  spbs: FederPAISpb[];
  indicadores: FederPAIIndicator[]; 
  isFetching: boolean;
  error: unknown;
  get: (opts?: { force?: boolean }) => Promise<void>;
  refetch: () => Promise<void>;
}

const FondoFederContext = createContext<FondoFederContextType | undefined>(undefined);

export const useFondoFeder = () => {
  const ctx = useContext(FondoFederContext);
  if (!ctx) throw new Error("useFondoFeder must be used within a FondoFederProvider");
  return ctx;
};

export function FondoFederProvider({ children }: { children: ReactNode }) {
  const { isLoggedIn, currentTenantId, userLang } = useAuth();
  const t = useTranslations();

  const [federPais, setFederPais] = useState<FederPAI[]>([]);
  const [projects, setProjects] = useState<FederPAIProject[]>([]);
  const [operaciones, setOperaciones] = useState<FederPAIOperaciones[]>([]);
  const [contratos, setContratos] = useState<FederPAIContratos[]>([]);
  const [transacciones, setTransacciones] = useState<FederPAITransacciones[]>([]);
  const [spbs, setSpbs] = useState<FederPAISpb[]>([]);
  const [indicadores, setIndicadores] = useState<FederPAIIndicator[]>([]); // ✅ keep raw
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const lastFetchedRef = useRef<number | null>(null);

  const SELECT_FIELDS = useMemo(
    () => [
      "*",
      "translations.*",
      "proyectos_feder.*",
      "proyectos_feder.translations.*",
      "proyectos_feder.feder_actuaciones.*",
      "proyectos_feder.feder_actuaciones.translations.*",
      "proyectos_feder.feder_actuaciones.feder_subactuaciones.*",
      "proyectos_feder.feder_actuaciones.feder_subactuaciones.financiacion_extra.*",
      "feder_operaciones.*",
      "feder_operaciones.translations.*",
      "feder_operaciones.folder.*",
      "feder_operaciones.folder.files.*",
      "feder_contratos.*",
      "feder_contratos.feder_operacion.*",
      "feder_contratos.translations.*",
      "feder_transacciones.*",
      "feder_transacciones.translations.*",
      "feder_transacciones.feder_spb.*",
      "feder_transacciones.feder_contrato.*",
      "feder_transacciones.feder_contrato.feder_operacion.*",
      "feder_transacciones.feder_contrato.feder_operacion.fondo_feder.*",
      "feder_spbs.*",
      "feder_indicadores.*",  
      "feder_indicadores.valores.*",              
      "feder_indicadores.feder_operacion.*", 
      "proyectos_feder.feder_actuaciones.folder.*",
      "proyectos_feder.feder_actuaciones.folder.files.*",
      "proyectos_feder.folder.*",
      "proyectos_feder.folder.files.*",
    ],
    []
  );

  const uniqueById = <T extends { id?: number }>(arr: T[]): T[] =>
    Array.from(new Map(arr.filter((x) => x?.id).map((x) => [x.id, x])).values());

  const fetchFondoFeder = useCallback(async (): Promise<void> => {
    if (!isLoggedIn || !currentTenantId) return;

    setIsFetching(true);
    setError(null);

    try {
      const data = await fetchElements<FederPAI>(
        "fondo_feder",
        SELECT_FIELDS,
        undefined,
        { tenant: { _eq: Number(currentTenantId) } }
      );

      const localizedPais = applyUserTranslationsAllTypes(data ?? [], userLang, ["nombre"]);

      const withOwningFondo = <T extends { fondo_feder?: FederPAI | number }>(
        rows: T[],
        fondo: FederPAI
      ): T[] => rows.map((row) => ({ ...row, fondo_feder: fondo }));

      const allProjects = localizedPais.flatMap((p) =>
        withOwningFondo(
          applyUserTranslationsAllTypes(p.proyectos_feder ?? [], userLang, ["nombre"]),
          p
        )
      );
      const allOperaciones = localizedPais.flatMap((p) =>
        withOwningFondo(
          applyUserTranslationsAllTypes(p.feder_operaciones ?? [], userLang, ["nombre"]),
          p
        )
      );
      const allContratos = localizedPais.flatMap((p) =>
        withOwningFondo(
          applyUserTranslationsAllTypes(
            p.feder_contratos ?? [],
            userLang,
            ["descripcion", "denominacion"]
          ),
          p
        )
      );
      const allTransacciones = localizedPais.flatMap((p) =>
        withOwningFondo(
          applyUserTranslationsAllTypes(
            p.feder_transacciones ?? [],
            userLang,
            ["descripcion"]
          ),
          p
        )
      );

      const allIndicadores = localizedPais.flatMap((p) =>
        (p.feder_indicadores ?? []).map((row) => ({ ...row, feder_fondo: p }))
      );
      const allSpbs = localizedPais.flatMap((p) =>
        withOwningFondo(p.feder_spbs ?? [], p)
      );

      setFederPais(localizedPais);
      setProjects(uniqueById(allProjects));
      setOperaciones(uniqueById(allOperaciones));
      setContratos(uniqueById(allContratos));
      setTransacciones(uniqueById(allTransacciones));
      setSpbs(uniqueById(allSpbs));
      setIndicadores(uniqueById(allIndicadores)); 

      lastFetchedRef.current = Date.now();
    } catch (err) {
      setError(err);
      setFederPais([]);
      setProjects([]);
      setOperaciones([]);
      setContratos([]);
      setTransacciones([]);
      setSpbs([]);
      setIndicadores([]); 

      toast.error(
        t("toast.feder.fetch-error-title", { default: "Error loading FEDER data" }),
        {
          description: t("toast.feder.fetch-error-desc", { default: "Please try again." }),
        }
      );
    } finally {
      setIsFetching(false);
    }
  }, [isLoggedIn, currentTenantId, t, SELECT_FIELDS, userLang]);

  const get = useCallback(
    async (opts?: { force?: boolean }) => {
      const force = opts?.force ?? false;
      if (!force && lastFetchedRef.current) {
        const stale = Date.now() - lastFetchedRef.current > 60_000;
        if (!stale) return;
      }
      await fetchFondoFeder();
    },
    [fetchFondoFeder]
  );

  const refetch = useCallback(() => get({ force: true }), [get]);

  useEffect(() => {
    if (!isLoggedIn || !currentTenantId) {
      setFederPais([]);
      setProjects([]);
      setOperaciones([]);
      setContratos([]);
      setTransacciones([]);
      setSpbs([]);
      setIndicadores([]); 
      return;
    }
    void fetchFondoFeder();
  }, [isLoggedIn, currentTenantId, fetchFondoFeder]);

  const value = useMemo<FondoFederContextType>(
    () => ({
      federPais,
      projects,
      operaciones,
      contratos,
      transacciones,
      spbs,
      indicadores, 
      isFetching,
      error,
      get,
      refetch,
    }),
    [
      federPais,
      projects,
      operaciones,
      contratos,
      transacciones,
      spbs,
      indicadores,
      isFetching,
      error,
      get,
      refetch,
    ]
  );

  return <FondoFederContext.Provider value={value}>{children}</FondoFederContext.Provider>;
}
